import Controller from "./application_controller"

/**
 * Lazily fills a <select> from a <template>.
 *
 * Direct usage:
 *
 *   <select
 *     data-controller="lazy-select"
 *     data-lazy-select-options-template-value="tax-rate-options">
 *     <option value="">—</option>
 *   </select>
 *
 *   <template id="tax-rate-options">
 *     <option value="">—</option>
 *     <option value="123">GST</option>
 *   </template>
 *
 * The select starts with only its current/blank option. On focus or mousedown the
 * full option list is cloned from the template while preserving the current value.
 *
 * Deferred usage:
 *
 *   <select
 *     data-deferred-controller="lazy-select"
 *     data-lazy-select-options-template-value="tax-rate-options">
 *   </select>
 *
 * Pair deferred usage with deferred_controller on an ancestor. This avoids
 * connecting a Stimulus controller for every select during large initial renders.
 *
 * Legacy code can seed the visible option before the controller connects:
 *
 *   window.MinuteDock.LazySelectController.seedInitialValue(select, value)
 */
class LazySelectController extends Controller {
  static values = {
    optionsTemplate: String
  }

  static seedInitialValue(element, value) {
    const stringValue = value ? value.toString() : ""
    const option = document.createElement("option")
    option.value = stringValue
    option.text = stringValue ? this.optionText(element, stringValue) : ""
    option.selected = true

    element.replaceChildren(option)
    element.value = stringValue
    delete element.dataset.lazySelectLoaded
  }

  static optionText(element, value) {
    const templateElement = document.getElementById(element.dataset.lazySelectOptionsTemplateValue)
    if (!templateElement) return value

    const selectedOption = templateElement.content.querySelector(`option[value="${CSS.escape(value)}"]`)
    return selectedOption?.text || value
  }

  static deferredControllerAttached(element) {
    this.populateElement(element)
  }

  static populateElement(element) {
    if (element.dataset.lazySelectLoaded == "true") return

    const templateElement = document.getElementById(element.dataset.lazySelectOptionsTemplateValue)
    if (!templateElement) return

    const value = element.value
    element.replaceChildren(templateElement.content.cloneNode(true))
    element.value = value
    element.dataset.lazySelectLoaded = "true"
  }

  connect() {
    this.addEventListener("focus", this.populate.bind(this))
    this.addEventListener("mousedown", this.populate.bind(this))
  }

  populate() {
    this.constructor.populateElement(this.element)
  }
}

window.MinuteDock ??= {}
window.MinuteDock.LazySelectController = LazySelectController

export default LazySelectController
